import { useCallback, useMemo } from 'react';
import { ContentState, convertFromHTML, EditorState, Modifier } from 'draft-js';
import DOMPurify from 'dompurify';
import { useActions } from '../../provider/actions';
import { isUrl } from '../../../../helpers/isUrl';

const headerTypes = new Set(['header-one', 'header-two', 'header-three']);

export const useHandlers = () => {
  const { onChange } = useActions();

  const handleBeforeInput = useCallback(
    (chars, state) => {
      return chars === ' '
        ? turnWordIntoLink({ state, endChar: chars, cb: onChange })
        : 'not-handled';
    },
    [onChange]
  );

  const handleKeyCommand = useCallback(
    (command, state) => {
      return command === 'split-block'
        ? turnWordIntoLink({ state, endChar: '\n', cb: onChange })
        : 'not-handled';
    },
    [onChange]
  );

  const handlePastedText = useCallback(
    (_text, html, state) => {
      if (!html) return 'not-handled';

      const safeHtml = sanitizeHtml(html);
      if (!safeHtml) return 'not-handled';

      const { contentBlocks, entityMap } = convertFromHTML(safeHtml);

      const { getBlockMap } = ContentState.createFromBlockArray(
        contentBlocks,
        entityMap
      );

      const newContent = Modifier.replaceWithFragment(
        state.getCurrentContent(),
        state.getSelection(),
        getBlockMap()
      );

      const newContentWithZeroSpace = Modifier.insertText(
        newContent,
        newContent.getSelectionAfter(),
        '\u200B'
      );

      const newState = EditorState.push(
        state,
        newContentWithZeroSpace,
        'insert-characters'
      );

      onChange(newState);

      return 'handled';
    },
    [onChange]
  );

  const handleReturn = useCallback(
    (_e, state) => {
      const selection = state.getSelection();

      if (!selection.isCollapsed()) {
        return 'not-handled';
      }

      const content = state.getCurrentContent();
      const block = content.getBlockForKey(selection.getStartKey());
      const blockType = block.getType();

      if (headerTypes.has(blockType)) {
        const newContent = Modifier.splitBlock(content, selection);
        const withNewBlock = EditorState.push(state, newContent, 'split-block');

        const targetSelection = withNewBlock.getSelection().merge({
          anchorOffset: 0,
          focusOffset: 0,
        });

        const withoutStyle = Modifier.setBlockType(
          withNewBlock.getCurrentContent(),
          targetSelection,
          'unstyled'
        );

        const withClearedStyle = EditorState.push(
          withNewBlock,
          withoutStyle,
          'change-block-type'
        );

        onChange(withClearedStyle);

        return 'handled';
      }

      return 'not-handled';
    },
    [onChange]
  );

  return useMemo(
    () => ({
      onChange,
      handleBeforeInput,
      handleKeyCommand,
      handlePastedText,
      handleReturn,
    }),
    [
      onChange,
      handleBeforeInput,
      handleKeyCommand,
      handlePastedText,
      handleReturn,
    ]
  );
};

function turnWordIntoLink({ state, endChar, cb }) {
  const selection = state.getSelection();
  const content = state.getCurrentContent();

  const anchorKey = selection.getAnchorKey();
  const start = selection.getStartOffset();
  const end = selection.getEndOffset();

  const currentBlock = content.getBlockForKey(anchorKey);
  const blockText = currentBlock.getText();

  if (blockText[start] !== ' ' && blockText[start] !== undefined) {
    return 'not-handled';
  }

  let wordStart = start;
  while (wordStart > 0 && blockText[wordStart - 1] !== ' ') {
    wordStart--;
  }

  const word = blockText.slice(wordStart, end);
  if (isUrl(word)) {
    const linkSelection = selection.merge({
      anchorOffset: wordStart,
      focusOffset: end,
    });

    const link = content.createEntity('LINK', 'MUTABLE', { url: word });
    const linkKey = link.getLastCreatedEntityKey();
    const linkContent = Modifier.applyEntity(content, linkSelection, linkKey);

    const endCharSelection = linkSelection.merge({
      anchorOffset: start,
      focusOffset: start,
    });

    const contentWithLinkAndEndChar = Modifier.insertText(
      linkContent,
      endCharSelection,
      endChar
    );

    const nextState = EditorState.push(
      state,
      contentWithLinkAndEndChar,
      'insert-characters'
    );
    cb(nextState);
    return 'handled';
  }

  return 'not-handled';
}

const REGEX_NBSP = new RegExp('&nbsp;', 'g');
const REGEX_IMG = new RegExp('<img[^>]*>', 'g');
function sanitizeHtml(html) {
  return DOMPurify.sanitize(
    html.trim().replace(REGEX_NBSP, ' ').replace(REGEX_IMG, '')
  );
}
